import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Layout } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { UserOctagon, More } from 'iconsax-react';

import YadLogo from '../../../assets/icons/YadLogo';
import CustomSidebarMenu from '../../atoms/CustomSidebarMenu';
import { usePermissions } from '../../../hooks/usePermissions';
import { ADMIN_ROLES } from '../../../constants/system';
import { ROUTE_CONFIG, getRouteAuthConfig } from '../../../constants/routes';
import { setPage, setSearch } from '../../../store/slices/publicSlice';

const { Sider } = Layout;

const siderStyle: React.CSSProperties = {
  overflow: 'auto',
  height: '100vh',
  position: 'sticky',
  insetInlineStart: 0,
  top: 0,
  bottom: 0,
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  backgroundColor: '#09090B',
  borderRight: '1px solid #18181B',
};

type MenuItem = Required<MenuProps>['items'][number];

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, checkAllPermissions, checkAnyPermission } = usePermissions();

  // 🔹 Handle navigation when clicking a menu item
  const handleMenuClick: MenuProps['onClick'] = (e) => {
    // Shared page state must not leak between different lists
    dispatch(setPage(0));
    dispatch(setSearch({ key: 'biddingPostsSearch', value: '' }));
    navigate(e.key);
  };

  const menuItems = useMemo(() => {
    const hasRequiredRole = (requiredRoles: string[]): boolean => {
      if (!user?.role?.code) return false;
      return requiredRoles.includes(user.role.code);
    };

    const hasRouteAccess = (
      requiredRoles?: string[],
      requiredPermissions?: string[],
      requireAllPermissions: boolean = false
    ): boolean => {
      if (user?.role?.code === ADMIN_ROLES.superAdmin) {
        return true;
      }

      if (requiredRoles && requiredRoles.length > 0) {
        if (!hasRequiredRole(requiredRoles)) {
          return false;
        }
      }

      if (requiredPermissions && requiredPermissions.length > 0) {
        if (requireAllPermissions) {
          if (!checkAllPermissions(requiredPermissions)) {
            return false;
          }
        } else {
          if (!checkAnyPermission(requiredPermissions)) {
            return false;
          }
        }
      }

      return true;
    };

    const convertToMenuItems = (
      routes: typeof ROUTE_CONFIG,
      isSubItem = false
    ): MenuItem[] => {
      return routes
        .filter((route) => !route.hiddenInMenu) // Filter out routes hidden from menu
        .map((route) => {
          const IconComponent = route.icon;

          const menuItem: MenuItem = {
            key: route.key,
            icon: IconComponent ? (
              <IconComponent size={isSubItem ? 18 : 20} />
            ) : undefined,
            label: t(route.labelKey),
            ...(route.type && { type: route.type }),
            ...(route.children && {
              children: convertToMenuItems(route.children, true),
            }),
          } as MenuItem;

          return menuItem;
        });
    };

    const allMenuItems = convertToMenuItems(ROUTE_CONFIG);

    const filterMenuItem = (item: MenuItem): MenuItem | null => {
      if (!item) return null;

      if ('children' in item && item.children && Array.isArray(item.children)) {
        const filteredChildren = item.children
          .map((child) => filterMenuItem(child as MenuItem))
          .filter((child): child is MenuItem => child !== null);

        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          ...item,
          children: filteredChildren,
        };
      }

      const routeKey = item.key as string;
      const accessConfig = getRouteAuthConfig(routeKey);

      if (!accessConfig) {
        return item;
      }

      const hasAccess = hasRouteAccess(
        accessConfig.requiredRoles,
        accessConfig.requiredPermissions,
        accessConfig.requireAllPermissions ?? false
      );

      return hasAccess ? item : null;
    };

    const filteredRoutes = allMenuItems
      .map((item) => filterMenuItem(item))
      .filter((item): item is MenuItem => item !== null);

    const menuGroup = filteredRoutes.filter((item) => item && item.key === '/');
    const userMgmtGroup = filteredRoutes.filter(
      (item) =>
        item &&
        (item.key === '/sellers' ||
          item.key === '/products' ||
          item.key === '/bidding-posts' ||
          item.key === '/support-tickets')
    );
    const adminConfigGroup = filteredRoutes.filter(
      (item) => item && item.key === 'admin-configurations'
    );
    const reportsGroup = filteredRoutes.filter(
      (item) => item && item.key === 'reports'
    );
    const jobManagementGroup = filteredRoutes.filter(
      (item) => item && item.key === 'job-management'
    );
    const systemActivityLogsGroup = filteredRoutes.filter(
      (item) => item && item.key === 'system-activity-logs'
    );
    const othersGroup = filteredRoutes.filter(
      (item) => item && item.key === '/reviews'
    );

    if (collapsed) {
      // In collapsed mode, return filtered routes without nested groups
      return filteredRoutes;
    }

    const structuredItems: MenuItem[] = [];

    // 1. Home / Dashboard direct item
    if (menuGroup.length > 0) {
      structuredItems.push(...menuGroup);
    }

    // 2. User Management Submenu
    if (userMgmtGroup.length > 0) {
      structuredItems.push({
        key: 'user-management-sub',
        icon: <UserOctagon size={20} />,
        label: t('home.user-management'),
        children: userMgmtGroup,
      } as MenuItem);
    }

    // 3. Admin Configurations Submenu
    if (adminConfigGroup.length > 0) {
      const adminConfig = adminConfigGroup[0] as MenuItem;
      structuredItems.push(adminConfig);
    }

    // 4. Reports Submenu
    if (reportsGroup.length > 0) {
      const reports = reportsGroup[0] as MenuItem;
      structuredItems.push(reports);
    }

    // 5. Job Management Submenu
    if (jobManagementGroup.length > 0) {
      const jobMgmt = jobManagementGroup[0] as MenuItem;
      structuredItems.push(jobMgmt);
    }

    // 6. System Activity Logs Submenu
    if (systemActivityLogsGroup.length > 0) {
      const systemActivityLogs = systemActivityLogsGroup[0] as MenuItem;
      structuredItems.push(systemActivityLogs);
    }

    // 7. Others Submenu
    if (othersGroup.length > 0) {
      structuredItems.push({
        key: 'others-sub',
        icon: <More size={20} />,
        label: t('home.others'),
        children: othersGroup,
      } as MenuItem);
    }

    return structuredItems;
  }, [t, user, checkAllPermissions, checkAnyPermission, collapsed]);

  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (path === '/') return ['/'];
    if (path.startsWith('/sellers')) return ['/sellers'];
    if (path.startsWith('/products')) return ['/products'];
    if (path.startsWith('/bidding-posts')) return ['/bidding-posts'];
    if (path.startsWith('/reviews')) return ['/reviews'];
    if (path.startsWith('/commission')) return ['/commission'];
    if (path.startsWith('/coins/services')) return ['/coins/services'];
    if (path.startsWith('/categories')) return ['/categories'];
    if (path.startsWith('/support-tickets')) return ['/support-tickets'];
    if (path.startsWith('/coins/offers')) return ['/coins/offers'];
    if (path.startsWith('/dispute')) return ['/dispute'];
    if (path.startsWith('/promotions-requests'))
      return ['/promotions-requests'];
    if (path.startsWith('/promotions-slots')) return ['/promotions-slots'];
    if (path.startsWith('/promotions-durations/home'))
      return ['/promotions-durations/home'];
    if (path.startsWith('/promotions-durations/category'))
      return ['/promotions-durations/category'];
    if (path.startsWith('/subscriptions-plans'))
      return ['/subscriptions-plans'];
    if (path.startsWith('/configuration-logs')) return ['/configuration-logs'];
    if (path.startsWith('/yad-default-promotions'))
      return ['/yad-default-promotions'];
    if (path.startsWith('/total-revenue')) return ['/total-revenue'];
    if (path.startsWith('/orders-budget')) return ['/orders-budget'];
    if (path.startsWith('/disputes-reports')) return ['/disputes-reports'];
    if (path.startsWith('/orders-reports')) return ['/orders-reports'];
    if (path.startsWith('/users-reports')) return ['/users-reports'];
    if (path.startsWith('/subscription-reports'))
      return ['/subscription-reports'];
    if (path.startsWith('/reports/promotions-reports'))
      return ['/reports/promotions-reports'];
    if (path.startsWith('/logistics-feedback')) return ['/logistics-feedback'];
    if (path.startsWith('/disliked-jobs')) return ['/disliked-jobs'];
    return [path];
  }, [location.pathname]);

  // Determine active parent submenu from pathname
  const activeSubMenuKey = useMemo(() => {
    const path = location.pathname;
    if (
      path.startsWith('/sellers') ||
      path.startsWith('/products') ||
      path.startsWith('/support-tickets')
    ) {
      return 'user-management-sub';
    }
    if (
      path.startsWith('/commission') ||
      path.startsWith('/coins/') ||
      path.startsWith('/categories') ||
      path.startsWith('/dispute') ||
      path.startsWith('/promotions-') ||
      path.startsWith('/subscriptions-plans') ||
      path.startsWith('/yad-default-promotions')
    ) {
      return 'admin-configurations';
    }
    if (
      path.startsWith('/total-revenue') ||
      path.startsWith('/orders-budget') ||
      path.startsWith('/disputes-reports') ||
      path.startsWith('/orders-reports') ||
      path.startsWith('/users-reports') ||
      path.startsWith('/subscription-reports') ||
      path.startsWith('/reports/')
    ) {
      return 'reports';
    }
    if (
      path.startsWith('/bidding-posts') ||
      path.startsWith('/disliked-jobs') ||
      path.startsWith('/jobs-management')
    ) {
      return 'job-management';
    }
    if (
      path.startsWith('/logistics-feedback') ||
      path.startsWith('/configuration-logs') ||
      path.startsWith('/configurations-audit-logs')
    ) {
      return 'system-activity-logs';
    }
    if (path.startsWith('/reviews')) {
      return 'others-sub';
    }
    return null;
  }, [location.pathname]);

  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    return activeSubMenuKey ? [activeSubMenuKey] : [];
  });

  // Keep active section open when route changes
  useEffect(() => {
    if (activeSubMenuKey) {
      setOpenKeys([activeSubMenuKey]);
    }
  }, [activeSubMenuKey]);

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    if (!collapsed) {
      setOpenKeys(keys);
    }
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      width={280}
      theme="dark"
      style={siderStyle}
      className="!bg-[#09090B] [&::-webkit-scrollbar]:hidden"
      data-testid="admin-sidebar"
    >
      {/* 🔹 Logo Section */}
      <div
        className="h-20 flex items-center justify-start px-6 mb-2"
        data-testid="admin-sidebar-logo-container"
      >
        {!collapsed ? (
          <div
            className="flex items-center gap-2"
            data-testid="admin-sidebar-logo"
          >
            <div
              style={{ filter: 'invert(1) hue-rotate(180deg) brightness(1.5)' }}
              className="flex items-center"
              data-testid="admin-sidebar-logo-icon"
            >
              <YadLogo />
            </div>
          </div>
        ) : (
          <div
            className="w-8 h-8 bg-pink-600 rounded-lg flex items-center justify-center text-white font-bold mx-auto"
            data-testid="admin-sidebar-collapsed-logo"
          >
            Y
          </div>
        )}
      </div>

      {/* 🔹 Menu Section */}
      <div
        className="h-[calc(100vh-140px)] overflow-y-auto [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        data-testid="admin-sidebar-menu-container"
      >
        <CustomSidebarMenu
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={selectedKeys}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={handleOpenChange}
          onClick={handleMenuClick}
          items={menuItems}
          className="border-r-0 !bg-[#09090B]"
          itemColor="#ACACAC"
          itemSelectedColor="#FFFFFF"
          itemSelectedBg="#1E1E21"
          itemHoverColor="#FFFFFF"
          itemHoverBg="rgba(255, 255, 255, 0.05)"
          popupBg="#18181B"
          fontSize={15}
          data-testid="admin-sidebar-menu"
        />
      </div>
    </Sider>
  );
};

export default Sidebar;
